import deepmerge from 'deepmerge';

const getWindowConfig = () => {
  if (typeof window === 'undefined') {
    return {};
  }
  const config = window?.GAPP_CONFIG || {};
  return {
    targetEnv: config.targetEnv,
    baseUrl: config.baseUrl,
    uiVersion: config.uiVersion,
  };
};

const isEnabledValue = (value) =>
  ['1', 'true', 'yes'].includes(String(value || '').toLowerCase());

const normalizeOptionalValue = (value) => {
  const normalizedValue = value?.trim();
  return normalizedValue || undefined;
};

const configs = {
  development: {
    env: 'dev',
    analytics: {
      testMode: false,
      tagManager: {
        id: 'GTM-5H8PBDF7',
      },
    },
    baseUrl:
      process.env.BASE_URL ||
      getWindowConfig().baseUrl ||
      'http://localhost:3000',
    datadogRum: {
      enabled: false,
    },
    liveChat: {
      enabled: isEnabledValue(
        process.env.NEXT_PUBLIC_GETAPP_TEXTCOM_LIVE_CHAT_ENABLED
      ),
      organizationId:
        normalizeOptionalValue(
          process.env.NEXT_PUBLIC_GETAPP_TEXTCOM_LIVE_CHAT_ORGANIZATION_ID
        ) || '',
      group: normalizeOptionalValue(
        process.env.NEXT_PUBLIC_GETAPP_TEXTCOM_LIVE_CHAT_GROUP
      ),
      chatBetweenGroups: isEnabledValue(
        process.env.NEXT_PUBLIC_GETAPP_TEXTCOM_LIVE_CHAT_BETWEEN_GROUPS
      ),
      // OAuth client registered in the text.com Developer Console for the
      // Customer SDK conversation UI (bx-live-chat-sdk) — a different
      // credential from the native widget's organizationId/group pair above,
      // which authenticates via the widget's own embedded script instead.
      // Shared across brands (confirmed during the engineering spike), not
      // per-brand like group.
      customerSdkClientId: normalizeOptionalValue(
        process.env.NEXT_PUBLIC_GETAPP_TEXTCOM_CUSTOMER_SDK_CLIENT_ID
      ),
    },
    snowPlow: {
      enabled: false,
      collectorUrl:
        'https://com-gartner-dev1.mini.snplow.net/com.snowplowanalytics.snowplow/tp2',
      env: 'dev',
    },
    googleClientId:
      '314165181912-7ak92mneo252s40q0pu6ci9adni0qvin.apps.googleusercontent.com',
    researchBoard: {
      landingUrl:
        process.env.NEXT_PUBLIC_G2_RESEARCH_BOARD_LANDING_URL ||
        'https://www.g2.com/assistant/landing',
    },
    userWorkspace: {
      url:
        process.env.NEXT_PUBLIC_G2_USER_WORKSPACE_URL ||
        'https://www-ue-staging-5.g2.com/users/~',
    },
  },
  staging: {
    env: 'staging',
    datadogRum: {
      enabled: false,
    },
    snowPlow: {
      enabled: false,
      collectorUrl:
        'https://com-gartner-dev1.mini.snplow.net/com.snowplowanalytics.snowplow/tp2',
      env: 'staging',
    },
    googleClientId:
      '314165181912-7ak92mneo252s40q0pu6ci9adni0qvin.apps.googleusercontent.com',
    researchBoard: {
      landingUrl:
        process.env.NEXT_PUBLIC_G2_RESEARCH_BOARD_LANDING_URL ||
        'https://www.g2.com/assistant/landing',
    },
    userWorkspace: {
      url:
        process.env.NEXT_PUBLIC_G2_USER_WORKSPACE_URL ||
        'https://www-ue-staging-5.g2.com/users/~',
    },
  },
  qa: {
    env: 'qa',
    datadogRum: {
      enabled: false,
    },
    snowPlow: {
      enabled: true,
      collectorUrl:
        'https://com-gartner-dev1.mini.snplow.net/com.snowplowanalytics.snowplow/tp2',
      env: 'staging',
    },
    googleClientId:
      '314165181912-7ak92mneo252s40q0pu6ci9adni0qvin.apps.googleusercontent.com',
    researchBoard: {
      landingUrl:
        process.env.NEXT_PUBLIC_G2_RESEARCH_BOARD_LANDING_URL ||
        'https://www.g2.com/assistant/landing',
    },
    userWorkspace: {
      url:
        process.env.NEXT_PUBLIC_G2_USER_WORKSPACE_URL ||
        'https://www-ue-staging-5.g2.com/users/~',
    },
  },
  production: {
    env: 'prod',
    datadogRum: {
      enabled: true,
      applicationId: '79ad4916-96d4-407a-b144-ab273a278912',
      clientToken: 'pub56d9ead8931e06abcaf4ba1a1a1766cf',
    },
    snowPlow: {
      enabled: true,
      collectorUrl: 'https://data.getapp.com/events',
      env: 'prod',
    },
    googleClientId:
      '314165181912-rd6t36g0mb6bu62g0vqlvg86eb65ffeb.apps.googleusercontent.com',
    researchBoard: {
      landingUrl:
        process.env.NEXT_PUBLIC_G2_RESEARCH_BOARD_LANDING_URL ||
        'https://www.g2.com/assistant/landing',
    },
    userWorkspace: {
      url:
        process.env.NEXT_PUBLIC_G2_USER_WORKSPACE_URL ||
        'https://www.g2.com/users/~',
    },
  },
};

let currentConfig;

/** @type {import('./types').getPublicConfig} */
const getConfig = () => {
  if (currentConfig) {
    return currentConfig;
  }
  const targetEnv = process.env.TARGET_ENV || getWindowConfig().targetEnv;
  const merged = deepmerge(configs.development, configs[targetEnv] || {});

  // Resolve version lazily — window.GAPP_CONFIG isn't available at module
  // init time during SSR, so reading it here ensures the client picks it up.
  if (merged.datadogRum?.enabled) {
    merged.datadogRum.version = getWindowConfig().uiVersion || undefined;
  }

  // Only cache on the client — server calls lack window-dependent values
  // and each request may target a different environment.
  if (typeof window !== 'undefined') {
    currentConfig = merged;
  }

  return merged;
};

export default getConfig;
